/**
 * Compliance Utilities - Helper functions for regulatory compliance
 */

import type {
  RegulatoryMapping,
  AIVSSMetric,
  CompanyPolicyRequirement,
  ComplianceStatus,
  NISTFunction,
  EURiskAssessment,
  ComplianceGap,
  ComplianceStats,
  ScanDataForCompliance,
} from '@/types/compliance';

import { regulatoryMappingDefinitions } from '@/data/complianceData';

export function getEUAIActRiskLevel(decisionCriticality: number): EURiskAssessment {
  if (decisionCriticality >= 8) {
    return {
      level: 'critical',
      euCategory: 'Unacceptable Risk',
      description: 'AI system poses unacceptable risk and may be prohibited under EU AI Act',
    };
  }
  if (decisionCriticality >= 6) {
    return {
      level: 'high',
      euCategory: 'High Risk',
      description: 'AI system classified as high-risk requiring conformity assessment',
    };
  }
  if (decisionCriticality >= 3) {
    return {
      level: 'medium',
      euCategory: 'Limited Risk',
      description: 'AI system has transparency obligations under EU AI Act',
    };
  }
  return {
    level: 'low',
    euCategory: 'Minimal Risk',
    description: 'AI system classified as minimal risk with voluntary codes of conduct',
  };
}

export function getNISTFunctionColor(func: NISTFunction): string {
  switch (func) {
    case 'GOVERN':
      return 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30';
    case 'MAP':
      return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
    case 'MEASURE':
      return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
    case 'MANAGE':
      return 'text-green-400 bg-green-500/20 border-green-500/30';
    default:
      return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
  }
}

export function calculateComplianceStats(mappings: RegulatoryMapping[]): {
  euStats: ComplianceStats;
  nistStats: ComplianceStats;
} {
  const euStats: ComplianceStats = { compliant: 0, partial: 0, nonCompliant: 0 };
  const nistStats: ComplianceStats = { compliant: 0, partial: 0, nonCompliant: 0 };

  for (const mapping of mappings) {
    switch (mapping.euAIAct.status) {
      case 'compliant':
        euStats.compliant++;
        break;
      case 'partial':
        euStats.partial++;
        break;
      case 'non-compliant':
        euStats.nonCompliant++;
        break;
    }

    switch (mapping.nistAIRMF.status) {
      case 'compliant':
        nistStats.compliant++;
        break;
      case 'partial':
        nistStats.partial++;
        break;
      case 'non-compliant':
        nistStats.nonCompliant++;
        break;
    }
  }

  return { euStats, nistStats };
}

export function getAllComplianceGaps(mappings: RegulatoryMapping[]): ComplianceGap[] {
  const gaps: ComplianceGap[] = [];

  for (const mapping of mappings) {
    for (const gap of mapping.euAIAct.gaps) {
      gaps.push({
        framework: 'EU AI Act',
        article: mapping.euAIAct.articles.join(', '),
        gap,
        severity: mapping.euAIAct.status === 'non-compliant' ? 'high' : 'medium',
      });
    }

    for (const gap of mapping.nistAIRMF.gaps) {
      gaps.push({
        framework: 'NIST AI RMF',
        article: mapping.nistAIRMF.categories.join(', '),
        gap,
        severity: mapping.nistAIRMF.status === 'non-compliant' ? 'high' : 'medium',
      });
    }
  }

  return gaps;
}

export function evaluatePolicyRequirement(
  requirement: CompanyPolicyRequirement,
  metrics: AIVSSMetric[]
): ComplianceStatus {
  const metric = metrics.find((m) => m.id === requirement.mappedMetric);
  if (!metric) return 'not-applicable';

  const normalized = metric.value / metric.maxValue;

  switch (requirement.operator) {
    case 'gte':
      return normalized >= requirement.threshold ? 'compliant' : 'non-compliant';
    case 'lte':
      return normalized <= requirement.threshold ? 'compliant' : 'non-compliant';
    case 'eq':
      return Math.abs(normalized - requirement.threshold) < 0.01 ? 'compliant' : 'non-compliant';
    default:
      return 'not-applicable';
  }
}

// ---------------------------------------------------------------------------
// Derive compliance metrics and regulatory mappings from real scan data
// ---------------------------------------------------------------------------

const METRIC_DEFINITIONS: Array<{
  id: string;
  name: string;
  description: string;
  higherIsBetter: boolean;
}> = [
  { id: 'data-sensitivity', name: 'Data Sensitivity', description: 'Measures the sensitivity of data processed by the AI system', higherIsBetter: false },
  { id: 'model-robustness', name: 'Model Robustness', description: 'Evaluates the model resistance to adversarial attacks', higherIsBetter: true },
  { id: 'decision-criticality', name: 'Decision Criticality', description: 'Assesses the impact of AI decisions on individuals', higherIsBetter: false },
  { id: 'autonomy-level', name: 'Autonomy Level', description: 'Degree of independent decision-making', higherIsBetter: false },
  { id: 'transparency', name: 'Transparency', description: 'Explainability and interpretability of AI decisions', higherIsBetter: true },
  { id: 'human-oversight', name: 'Human Oversight', description: 'Quality of human supervision mechanisms', higherIsBetter: true },
  { id: 'bias-fairness', name: 'Bias & Fairness', description: 'Measures discriminatory patterns and fairness', higherIsBetter: true },
  { id: 'data-governance', name: 'Data Governance', description: 'Quality of data management practices', higherIsBetter: true },
  { id: 'security-controls', name: 'Security Controls', description: 'Strength of security measures', higherIsBetter: true },
  { id: 'accountability', name: 'Accountability', description: 'Clear responsibility and liability frameworks', higherIsBetter: true },
];

function clamp(v: number): number {
  return Math.min(10, Math.max(0, v));
}

function aarsField(scan: ScanDataForCompliance, key: string): number {
  return scan.aarsBreakdown?.[key]?.score ?? 5.0;
}

function deriveMetricValue(id: string, scan: ScanDataForCompliance): number {
  const fs = scan.findingSummary;
  const total = Math.max(fs?.total ?? 1, 1);
  const scores = scan.scores;

  switch (id) {
    case 'data-sensitivity':
      return clamp(((fs?.high ?? 0) + (fs?.critical ?? 0)) / total * 10);
    case 'model-robustness':
      return clamp(10 - Math.max(scores.aivss, scores.cvss));
    case 'decision-criticality':
      return clamp(aarsField(scan, 'decisionAuthority'));
    case 'autonomy-level':
      return clamp((10 - aarsField(scan, 'humanInLoop') + aarsField(scan, 'decisionAuthority')) / 2);
    case 'transparency':
      return clamp(aarsField(scan, 'transparency'));
    case 'human-oversight':
      return clamp(aarsField(scan, 'humanInLoop'));
    case 'bias-fairness':
      return 5.0; // no data source available
    case 'data-governance': {
      const weighted = ((fs?.medium ?? 0) + (fs?.high ?? 0) + (fs?.critical ?? 0)) * 2;
      return clamp(10 - (weighted / total) * 10);
    }
    case 'security-controls':
      return clamp(10 - scores.cvss);
    case 'accountability':
      return clamp((aarsField(scan, 'errorHandling') + aarsField(scan, 'transparency')) / 2);
    default:
      return 5.0;
  }
}

function statusForMetric(
  value: number,
  higherIsBetter: boolean,
): ComplianceStatus {
  if (higherIsBetter) {
    if (value >= 7.0) return 'compliant';
    if (value >= 4.0) return 'partial';
    return 'non-compliant';
  }
  // Higher-is-riskier
  if (value <= 3.0) return 'compliant';
  if (value <= 6.0) return 'partial';
  return 'non-compliant';
}

const GAP_MESSAGES: Record<string, Record<ComplianceStatus, string>> = {
  'data-sensitivity': {
    'non-compliant': 'High proportion of critical/high findings — data handling requires immediate review',
    'partial': 'Elevated data sensitivity — additional data governance controls recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'model-robustness': {
    'non-compliant': 'Model robustness critically low — adversarial testing and hardening required',
    'partial': 'Model robustness below target — additional robustness testing recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'decision-criticality': {
    'non-compliant': 'Decision criticality exceeds acceptable threshold — risk classification and impact assessment required',
    'partial': 'Moderate decision criticality — formal risk classification documentation recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'autonomy-level': {
    'non-compliant': 'Autonomy level too high — override mechanisms and human-in-the-loop controls needed',
    'partial': 'Elevated autonomy — override mechanism documentation recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'transparency': {
    'non-compliant': 'Transparency score below minimum requirement — explainability documentation needed',
    'partial': 'Transparency partially meets requirements — additional documentation recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'human-oversight': {
    'non-compliant': 'Human oversight critically insufficient — formal oversight procedures required',
    'partial': 'Human oversight needs improvement — oversight procedures should be formalized',
    'compliant': '',
    'not-applicable': '',
  },
  'bias-fairness': {
    'non-compliant': 'Bias testing not performed — fairness assessment required',
    'partial': 'Bias testing not performed — fairness metrics should be defined and tested',
    'compliant': '',
    'not-applicable': '',
  },
  'data-governance': {
    'non-compliant': 'Data governance critically deficient — data management practices need overhaul',
    'partial': 'Data governance partially meets standards — data quality criteria need strengthening',
    'compliant': '',
    'not-applicable': '',
  },
  'security-controls': {
    'non-compliant': 'Security controls critically weak — immediate cybersecurity remediation required',
    'partial': 'Security controls need strengthening — additional cybersecurity measures recommended',
    'compliant': '',
    'not-applicable': '',
  },
  'accountability': {
    'non-compliant': 'Accountability structures critically lacking — responsibility and liability frameworks required',
    'partial': 'Accountability structures need formalization — documentation requirements partially met',
    'compliant': '',
    'not-applicable': '',
  },
};

export function deriveComplianceFromScan(scan: ScanDataForCompliance): {
  metrics: AIVSSMetric[];
  mappings: RegulatoryMapping[];
} {
  const metrics: AIVSSMetric[] = METRIC_DEFINITIONS.map((def) => ({
    id: def.id,
    name: def.name,
    value: Math.round(deriveMetricValue(def.id, scan) * 10) / 10,
    maxValue: 10,
    description: def.description,
  }));

  const mappings: RegulatoryMapping[] = regulatoryMappingDefinitions.map((defn) => {
    const metricDef = METRIC_DEFINITIONS.find((m) => m.id === defn.metricId)!;
    const metric = metrics.find((m) => m.id === defn.metricId)!;
    const status = statusForMetric(metric.value, metricDef.higherIsBetter);

    const gapMsg = GAP_MESSAGES[defn.metricId]?.[status] || '';
    const gaps = gapMsg ? [gapMsg] : [];

    return {
      metricId: defn.metricId,
      euAIAct: {
        articles: [...defn.euAIAct.articles],
        requirements: [...defn.euAIAct.requirements],
        status,
        gaps: [...gaps],
      },
      nistAIRMF: {
        functions: [...defn.nistAIRMF.functions] as NISTFunction[],
        categories: [...defn.nistAIRMF.categories],
        status,
        gaps: [...gaps],
      },
    };
  });

  return { metrics, mappings };
}
